import ChevronRightIcon from "@/assets/icons/admin/chevron-right.svg?react";
import SearchIcon from "@/assets/icons/admin/search.svg?react";
import type { AdminVisitRow } from "@/lib/admin-dashboard";
import { cn } from "@/lib/utils";

type AdminVisitsTableProps = {
  visits: AdminVisitRow[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  search: string;
  isLoading?: boolean;
  onSearchChange: (value: string) => void;
  onPageChange: (page: number) => void;
};

export function AdminVisitsTable({
  visits,
  totalCount,
  currentPage,
  totalPages,
  search,
  isLoading = false,
  onSearchChange,
  onPageChange,
}: AdminVisitsTableProps) {
  return (
    <div className="flex w-full flex-col gap-4">
      <section className="overflow-hidden rounded-xl border border-white/5 bg-[#132136]">
        <div className="flex flex-wrap items-center gap-4 px-6 py-5">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <h2 className="text-lg font-bold text-[#fcfcfc]">Total Visits</h2>
            <span className="rounded-2xl bg-[rgba(161,161,161,0.1)] px-2 py-0.5 text-xs font-bold text-[#38bdf8]">
              {totalCount}
            </span>
          </div>

          <label className="flex h-[43px] w-full max-w-[267px] items-center gap-2 rounded-lg border border-white/5 bg-[#1d2e46] px-4">
            <SearchIcon aria-hidden className="size-4 shrink-0" />
            <span className="sr-only">Search visits</span>
            <input
              type="search"
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder="Search..."
              className="w-full bg-transparent text-base text-white outline-none placeholder:text-[#9194a1]"
            />
          </label>
        </div>

        <div className="overflow-x-auto">
          <div className="min-w-[1286px]">
            <div className="flex items-center border-b border-white/5 bg-[#091424] px-6 py-3.5 text-xs font-bold uppercase tracking-[0.5px] text-[#9194a1]">
              <p className="w-[120px] shrink-0">Session ID</p>
              <p className="w-[84px] shrink-0">Region</p>
              <p className="w-[176px] shrink-0">Sector</p>
              <p className="w-[160px] shrink-0">Email</p>
              <p className="w-[160px] shrink-0">Organisation</p>
              <p className="min-w-0 flex-1">Current Step</p>
              <p className="w-[110px] shrink-0">Status</p>
              <p className="w-[100px] shrink-0">TCI Score</p>
              <p className="w-[120px] shrink-0 text-right">Last Seen</p>
            </div>

            {isLoading && visits.length === 0 ? (
              <p className="px-6 py-8 text-sm text-[#9194a1]">
                Loading visits…
              </p>
            ) : null}

            {!isLoading && visits.length === 0 ? (
              <p className="px-6 py-8 text-sm text-[#9194a1]">
                No visits match your filters.
              </p>
            ) : null}

            {visits.map((visit) => (
              <div
                key={visit.id}
                className="flex items-center border-b border-white/5 px-6 py-4"
              >
                <div className="flex w-[120px] shrink-0 flex-col gap-1">
                  <p className="text-sm font-bold text-white">
                    {visit.sessionId}
                  </p>
                  <p className="text-xs text-[#9194a1]">{visit.relativeTime}</p>
                </div>
                <div className="w-[84px] shrink-0">
                  <p className="truncate text-sm font-medium text-white">
                    {visit.region}
                  </p>
                </div>
                <div className="w-[176px] shrink-0 pr-2">
                  <p
                    className="truncate text-base font-medium tracking-[-0.4px] text-white"
                    title={visit.sector}
                  >
                    {visit.sector}
                  </p>
                </div>
                <div className="w-[160px] shrink-0 pr-2">
                  <p className="truncate text-xs text-[#9194a1]">
                    {visit.email}
                  </p>
                </div>
                <div className="w-[160px] shrink-0 pr-2">
                  <p
                    className="truncate text-sm font-medium text-white"
                    title={visit.organisationName}
                  >
                    {visit.organisationName}
                  </p>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-white">
                    {visit.currentStep}
                  </p>
                </div>
                <div className="w-[110px] shrink-0">
                  <span className="inline-flex rounded-md bg-[rgba(234,179,8,0.12)] px-2 py-1 text-xs font-bold text-[#eab308]">
                    {visit.status}
                  </span>
                </div>
                <div className="w-[100px] shrink-0">
                  <p className="text-sm font-bold text-white">
                    {visit.tciScore}
                  </p>
                </div>
                <div className="w-[120px] shrink-0 text-right">
                  <p className="text-xs text-[#9194a1]">
                    {visit.lastSeenLabel}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {totalCount > 0 ? (
        <AdminPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      ) : null}
    </div>
  );
}

type AdminPaginationProps = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
};

function AdminPagination({
  currentPage,
  totalPages,
  onPageChange,
}: AdminPaginationProps) {
  const pages = buildPageItems(currentPage, totalPages);

  return (
    <nav
      aria-label="Visits pagination"
      className="flex items-center justify-end gap-6 pr-0 sm:pr-8"
    >
      <button
        type="button"
        aria-label="Previous page"
        disabled={currentPage <= 1}
        className="flex size-8 items-center justify-center rounded bg-[#132136] text-white disabled:opacity-40"
        onClick={() => onPageChange(currentPage - 1)}
      >
        <ChevronRightIcon aria-hidden className="size-4 rotate-180" />
      </button>

      <div className="flex items-center gap-2">
        {pages.map((page, index) =>
          page === "…" ? (
            <span
              key={`ellipsis-${index}`}
              className="flex size-8 items-center justify-center rounded bg-[#132136] text-sm font-semibold text-white"
            >
              …
            </span>
          ) : (
            <button
              key={page}
              type="button"
              aria-current={page === currentPage ? "page" : undefined}
              className={cn(
                "flex size-8 items-center justify-center rounded text-sm font-semibold text-white",
                page === currentPage ? "bg-[#38bdf8]" : "bg-[#132136]",
              )}
              onClick={() => onPageChange(page)}
            >
              {page}
            </button>
          ),
        )}
      </div>

      <button
        type="button"
        aria-label="Next page"
        disabled={currentPage >= totalPages}
        className="flex size-8 items-center justify-center rounded bg-white text-[#0c1b2e] disabled:opacity-40"
        onClick={() => onPageChange(currentPage + 1)}
      >
        <ChevronRightIcon aria-hidden className="size-4" />
      </button>
    </nav>
  );
}

function buildPageItems(
  currentPage: number,
  totalPages: number,
): Array<number | "…"> {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 2) {
    return [1, 2, "…", totalPages - 1, totalPages];
  }

  if (currentPage >= totalPages - 1) {
    return [1, 2, "…", totalPages - 1, totalPages];
  }

  return [1, currentPage, "…", totalPages - 1, totalPages];
}
